import {
  AppOfferListItem,
  isAppPersonalizedOffer,
  isAppPersonalizedOfferGroup,
} from 'state/loyalty/types';

import { sortOfferListItemsByAvailability } from './sort-offer-list-items';

export interface PersonalizedOfferListItems {
  hasMatchedItems: boolean;
  items: AppOfferListItem[];
}

export type TierGroupChildIdsByParentId = ReadonlyMap<string, readonly string[]>;

const EMPTY_TIER_GROUP_CHILD_IDS: TierGroupChildIdsByParentId = new Map();

const getOfferIdentifiers = (item: AppOfferListItem): string[] => {
  const identifiers: string[] = [];

  if ('cmsId' in item && typeof item.cmsId === 'string' && item.cmsId.length > 0) {
    identifiers.push(item.cmsId);
  }

  if (
    'loyaltyEngineId' in item &&
    typeof item.loyaltyEngineId === 'string' &&
    item.loyaltyEngineId.length > 0 &&
    !identifiers.includes(item.loyaltyEngineId)
  ) {
    identifiers.push(item.loyaltyEngineId);
  }

  return identifiers;
};

export const personalizeOfferListItems = (
  offerListItems: AppOfferListItem[],
  sortedSystemwideOffers: string[],
  tierGroupChildIdsByParentId: TierGroupChildIdsByParentId = EMPTY_TIER_GROUP_CHILD_IDS
): PersonalizedOfferListItems => {
  const personalizedItems: AppOfferListItem[] = [];
  const generalItemsById = new Map<string, AppOfferListItem>();

  for (const item of offerListItems) {
    if (isAppPersonalizedOffer(item) || isAppPersonalizedOfferGroup(item)) {
      personalizedItems.push(item);
      continue;
    }

    for (const identifier of getOfferIdentifiers(item)) {
      if (!generalItemsById.has(identifier)) {
        generalItemsById.set(identifier, item);
      }
    }
  }

  const matchedGeneralItems: AppOfferListItem[] = [];
  const matchedPayloadIds = new Set<string>();
  const matchedGeneralItemsSet = new Set<AppOfferListItem>();
  for (const payloadId of sortedSystemwideOffers) {
    if (matchedPayloadIds.has(payloadId)) {
      continue;
    }

    const tierGroupChildIds = tierGroupChildIdsByParentId.get(payloadId);
    const directItem = generalItemsById.get(payloadId);
    const matchedChildId = directItem
      ? undefined
      : tierGroupChildIds?.find(childId => generalItemsById.has(childId));
    const matchedItem =
      directItem ?? (matchedChildId ? generalItemsById.get(matchedChildId) : null);

    if (matchedItem && !matchedGeneralItemsSet.has(matchedItem)) {
      matchedGeneralItems.push(matchedItem);
      matchedGeneralItemsSet.add(matchedItem);
      matchedPayloadIds.add(payloadId);
    }
  }

  return {
    hasMatchedItems: matchedGeneralItems.length > 0,
    items: sortOfferListItemsByAvailability([...personalizedItems, ...matchedGeneralItems]),
  };
};
